import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { UserCog, Save, Lock } from 'lucide-react';

const FIELDS = [
  ['full_name', 'Full Name'], ['job_title', 'Job Title'], ['agency', 'Agency'],
  ['license_type', 'License Type'], ['license_number', 'License Number'], ['state', 'State'],
  ['email', 'Email Address'], ['phone', 'Phone Number'], ['preferred_contact', 'Preferred Contact Method'],
  ['supervision_schedule', 'Supervision Schedule'], ['years_experience', 'Years of Experience'],
];

const PortalFieldInstructor = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API_URL}/internship-portal/field-instructor`); setForm(res.data.field_instructor || {}); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API_URL}/internship-portal/field-instructor`, form); toast.success('Saved.'); }
    catch (e) { toast.error('Could not save.'); } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-gray-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="portal-field-instructor">
      <div className="bg-white rounded-2xl border p-6">
        <h3 className="font-serif text-lg font-bold text-primary mb-1 flex items-center gap-2"><UserCog className="w-5 h-5 text-secondary" /> Field Instructor Information</h3>
        <p className="text-sm text-gray-500 mb-5">Keep your field instructor's details for your own private records. This is never shared.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs text-gray-500 mb-1 block">{label}</Label>
              <Input value={form[k] || ''} onChange={(e) => setF(k, e.target.value)} data-testid={`fi-${k}`} />
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div><Label className="text-xs text-gray-500 mb-1 block">Practice Areas</Label><Textarea rows={2} value={form.practice_areas || ''} onChange={(e) => setF('practice_areas', e.target.value)} /></div>
          <div><Label className="text-xs text-gray-500 mb-1 block">Populations Served</Label><Textarea rows={2} value={form.populations_served || ''} onChange={(e) => setF('populations_served', e.target.value)} /></div>
        </div>
        <div className="mt-4"><Label className="text-xs text-gray-500 mb-1 block">Student Notes</Label><Textarea rows={3} value={form.notes || ''} onChange={(e) => setF('notes', e.target.value)} data-testid="fi-notes" /></div>
        <div className="mt-4"><Button className="bg-primary text-white" onClick={save} disabled={saving} data-testid="save-fi-btn"><Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}</Button></div>
      </div>
      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">This information is for your private records only. Your field instructor is not notified and cannot view or edit anything here.</p>
      </div>
    </div>
  );
};

export default PortalFieldInstructor;
